/**
 * Which answer the "keep web pages" card offers, and its pages (spec §3.3).
 * Pure helpers over the chat timeline — no React, no I/O.
 *
 * Web pages are read from the `workLog` tool results only — the field the
 * server persists and checks a keep request against — with the server's
 * filter (http(s), not KB, deduped by normalised URL; see
 * `_web_citations.web_sources`). Live `citations` are never used: a page
 * that is not in the stored row cannot be kept.
 */

import type { Citation, Message, TimelineEntry } from "../../../chat/types";

/** "Same page" key, like the server's `normalize_url`: lower-case scheme and
 *  host, no default port, no fragment, "/" for an empty path. Null for
 *  anything that is not an http(s) URL. */
export function normalizeUrl(raw: string | undefined): string | null {
  let url: URL;
  try {
    url = new URL((raw ?? "").trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (!url.hostname) return null;
  url.hash = "";
  return url.toString();
}

/** The answer's web pages as the server will accept them: work-log tool
 *  results that are web (or untyped), http(s), deduped by normalised URL;
 *  the first occurrence keeps its stored spelling. */
export function webSourcesOf(m: Message): Citation[] {
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const entry of m.workLog ?? []) {
    if (entry.kind !== "tool") continue;
    for (const c of entry.results ?? []) {
      const url = c.url?.trim();
      const key = normalizeUrl(url);
      if ((c.type ?? "web") !== "web" || !url || !key || seen.has(key)) continue;
      seen.add(key);
      out.push({ ...c, url });
    }
  }
  return out;
}

/** A page's domain for display, without "www." ("" when unparseable). */
export function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** "1 page" / "3 pages". */
export function pagesLabel(n: number): string {
  return `${n} ${n === 1 ? "page" : "pages"}`;
}

/** The answer's text (used to match a streamed answer to its stored row). */
export function answerTextOf(m: Message): string {
  if (typeof m.content === "string") return m.content;
  return m.content
    .map((p) => (p.type === "text" ? p.text : ""))
    .join("");
}

// Inline [doc:N] / [chat:id] markers: the stream hides them, the stored row
// keeps them.
const CITATION_MARKER = / ?\[(?:doc|chat):[^\]\s]+\]/g;

function comparable(text: string): string {
  return text.replace(CITATION_MARKER, "").replace(/\s+/g, " ").trim();
}

/** The stored row of a just-streamed answer, or null when it isn't stored
 *  yet. Strict: only the LATEST stored assistant row can match, and only when
 *  its text is the tail of what was streamed (a turn persists its final text
 *  segment; the stream also showed any text before its tools). So while
 *  answer N is still being saved, the previous web-backed answer N-1 is never
 *  mistaken for it. */
export function matchStoredAnswer(live: Message, stored: TimelineEntry[]): Message | null {
  for (let i = stored.length - 1; i >= 0; i--) {
    if (stored[i].role !== "assistant") continue;
    const row = stored[i] as Message;
    const rowText = comparable(answerTextOf(row));
    const ok =
      isPersistedId(row.id) &&
      webSourcesOf(row).length > 0 &&
      rowText.length > 0 &&
      comparable(answerTextOf(live)).endsWith(rowText);
    return ok ? row : null;
  }
  return null;
}

export interface WebAnswerToKeep {
  message: Message;
}

/** The answer the card should offer, or null. Only the LATEST assistant
 *  message counts: a newer answer replaces the card (and a newer answer
 *  without web sources hides it). Already-answered cards stay gone. */
export function findWebAnswerToKeep(history: TimelineEntry[]): WebAnswerToKeep | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry.role !== "assistant") continue;
    const message = entry as Message;
    if (message.knowledgePrompt || webSourcesOf(message).length === 0) return null;
    return { message };
  }
  return null;
}

/** A stored row id (UUID). A just-streamed answer carries a synthetic id
 *  until the history is reloaded, and cannot be targeted server-side. */
export function isPersistedId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}
